import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Link2, Profile2User, Video } from "iconsax-reactjs";
import InputFiel from "../../commons/InputFiel";
import Button from "../../commons/Button";
import type {Skyroom } from "../../../services/webServices/skyroom/types";
import { postSkyroom } from "../../../services/webServices/skyroom/post";
import { patchSkyroom } from "../../../services/webServices/skyroom/patch";

interface RoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  room?: Skyroom;
}

export default function SkyRoomModal({ isOpen, onClose, onSuccess, room }: RoomModalProps) {

  const isEditMode = !!room;

  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState("");
  const [name, setName] = useState("");
  const [maxUsers, setMaxUsers] = useState(0);
  const [guestLogin, setGuestLogin] = useState(false);
  const [guestLimit, setGuestLimit] = useState(0);
  const [opLoginFirst, setOpLoginFirst] = useState(false);

  useEffect(() => {
    if (room) {
      setTitle(room.title ?? "");
      setName(room.name ?? "");
      setMaxUsers(room.max_users ?? 0);
      setGuestLogin(room.guest_login ?? false);
      setGuestLimit(room.guest_limit ?? 0);
      setOpLoginFirst(room.op_login_first ?? false);
    } else {
      setTitle("");
      setName("");
      setMaxUsers(0);
      setGuestLogin(false);
      setGuestLimit(0);
      setOpLoginFirst(false);
    }
  }, [room]);

  const handleSubmit = async () => {
    if (!title.trim() || !name.trim() || maxUsers <= 0 || (guestLogin && guestLimit <= 0)) {
      toast.error("لطفا همه ورودی‌ها را پر کنید");
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && room?.id) {
        const res = await patchSkyroom({
          id: room.id,
          originalSkyroom: room,
          data:{
            title: title.trim(),
            name: name.trim(),
            max_users: maxUsers,
            guest_login: guestLogin,
            guest_limit: guestLimit,
            op_login_first: opLoginFirst,
          }
        });

        if (!res) return;

        toast.success("اتاق اسکای‌روم با موفقیت ویرایش شد");
      } else {
        const res = await postSkyroom({
          body:{
            title: title.trim(),
            name: name.trim(),
            max_users: maxUsers,
            guest_login: guestLogin,
            guest_limit: guestLimit,
            op_login_first: opLoginFirst,
          }
        });

        if (!res) return;

        toast.success("اتاق اسکای‌روم با موفقیت ساخته شد");
      }

      onSuccess();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <dialog open className="bg-white w-full max-w-[720px] rounded-xl p-6">
        <h2 className="text-xl font-bold text-gray-700 mb-6">
          {isEditMode ? "ویرایش اتاق" : "ایجاد اتاق"}
        </h2>

        <div className="space-y-4 text-right">
          <div>
            <label className="flex items-center gap-2 text-lg text-gray-600 mb-2">
              <Video size="22" />
              عنوان اتاق
            </label>
            <InputFiel
              as="input"
              size="lg"
              value={title}
              placeholder="عنوان اتاق را وارد کنید"
              disabled={false}
              maxLength={50}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-lg text-gray-600 mb-2">
              <Link2 size="22" />
              پیوند اتاق
            </label>
            <InputFiel
              as="input"
              size="lg"
              value={name}
              placeholder="room-link"
              disabled={false}
              maxLength={50}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-lg text-gray-600 mb-2">
              <Profile2User size="22" />
              حداکثر کاربران
            </label>
            <InputFiel
              as="input"
              type="number"
              size="lg"
              value={String(maxUsers)}
              placeholder="تعداد کاربران"
              disabled={false}
              maxLength={50}
              onChange={(e) => setMaxUsers(Number(e.target.value))}
            />
          </div>

          <div className="flex gap-6 pt-2">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={guestLogin}
                onChange={(e) => setGuestLogin(e.target.checked)}
                className="peer sr-only"
              />
              <span
                className="
                  flex items-center justify-center
                  h-5 w-5 rounded-md
                  border border-gray-300
                  transition-colors duration-150
                  peer-checked:border-primaryVar-500
                  peer-checked:bg-primaryVar-500
                  peer-focus-visible:ring-2
                  peer-focus-visible:ring-primaryVar-200
                "
              />
              <span className="text-base text-gray-700">
                ورود به عنوان مهمان
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={opLoginFirst}
                onChange={(e) => setOpLoginFirst(e.target.checked)}
                className="peer sr-only"
              />
              <span
                className="
                  flex items-center justify-center
                  h-5 w-5 rounded-md
                  border border-gray-300
                  transition-colors duration-150
                  peer-checked:border-primaryVar-500
                  peer-checked:bg-primaryVar-500
                  peer-focus-visible:ring-2
                  peer-focus-visible:ring-primaryVar-200
                "
              />
              <span className="text-base text-gray-700">
                ابتدا اپراتور وارد شود
              </span>
            </label>
          </div>

          <div>
            <label className="text-lg text-gray-600 mb-2 block">
              تعداد مهمانان
            </label>
            <InputFiel
              as="input"
              type="number"
              size="lg"
              value={String(guestLimit)}
              placeholder="تعداد مهمانان مجاز"
              disabled={!guestLogin}
              maxLength={50}
              onChange={(e) => setGuestLimit(Number(e.target.value))}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="gray"
              style="solid"
              size="lg"
              text="انصراف"
              disabled={false}
              loading={loading}
              onClick={onClose}
            />
            <Button
              variant="primary"
              style="solid"
              size="lg"
              text={isEditMode ? "ذخیره تغییرات" : "ایجاد اتاق"}
              disabled={false}
              loading={loading}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </dialog>
    </div>
  );
};